// src/context/PackageBookingContext.jsx
import axios from "axios";
import { createContext, useContext, useState } from "react";
import { showError } from "../utils/toastUtils";
import { useAuth } from "./AuthContext";

const PackageBookingContext = createContext(null);

const INITIAL_BOOKING_DATA = {
  packageId: null,
  slotAvailabilityId: null,
  slotSnapshot: null,
  location: {
    type: "Point",
    coordinates: [],
    address: {
      formatted: "",
      street: "",
      locality: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
  },
  __addressValid: false,
  __slotValid: false,
};

export const PackageBookingProvider = ({ children }) => {
  const { user } = useAuth(); // ← get user directly here, always fresh

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingData, setBookingData] = useState(INITIAL_BOOKING_DATA);
  const [submitting, setSubmitting] = useState(false);

  const openBookingModal = (pkg) => {
    setSelectedPackage(pkg);
    setBookingData({ ...INITIAL_BOOKING_DATA, packageId: pkg._id });
    setBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setBookingModalOpen(false);
    setSelectedPackage(null);
    setBookingData(INITIAL_BOOKING_DATA);
  };

  const submitPackageBooking = async (data) => {
    setSubmitting(true);

    // Resolve userId fresh from auth at call time — never rely on stale state
    const userId = user?.id || user?._id;

    if (!userId) {
      showError("Please log in to book a package");
      setSubmitting(false);
      return { success: false };
    }

    try {
      const payload = {
        userId,
        packageId: data.packageId,
        slotAvailabilityId: data.slotAvailabilityId,
        location: {
          type: "Point",
          coordinates: data.location.coordinates,
          address: data.location.address,
        },
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/package-bookings`,
        payload,
      );

      if (res.data?.success) {
        return { success: true, data: res.data.data };
      }

      showError(res.data?.message || "Booking failed");
      return { success: false };
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Booking failed. Please try again.";
      showError(msg);
      return { success: false, message: msg };
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PackageBookingContext.Provider
      value={{
        selectedPackage,
        bookingModalOpen,
        bookingData,
        setBookingData,
        submitting,
        openBookingModal,
        closeBookingModal,
        submitPackageBooking,
      }}
    >
      {children}
    </PackageBookingContext.Provider>
  );
};

export const usePackageBooking = () => {
  const ctx = useContext(PackageBookingContext);
  if (!ctx)
    throw new Error(
      "usePackageBooking must be used inside PackageBookingProvider",
    );
  return ctx;
};
